/**
 * Weekly calorie cycling ("zigzag", B-0138): the same weekly calories as a flat daily target, spread as a few higher days and lower days.
 * Pure; synced to the API (/v1/converters/tdee). The weekly total is the daily target x 7 and never changes, so the average is the same
 * as the flat plan; whether cycling suits a person better than a flat target is not something this tool claims (it is a scheduling choice).
 * A low day is never set under the safe minimum. That minimum (1500 men, 1200 women) is the app's long-standing floor in calorie.ts; it was NOT
 * re-checked against a published guideline in this change and is flagged as such in the notes.
 */
import type { CalcStep } from "@/lib/calc/steps";

export const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
/** Which weekdays are the higher days, spread out so two high days are not side by side. */
export const HIGH_DAY_INDEXES: Record<1 | 2 | 3, number[]> = { 1: [5], 2: [2, 5], 3: [0, 2, 5] };

export interface CycleDay { day: string; kcal: number; high: boolean }
export interface CyclePlan {
  ok: boolean;
  reason?: string;
  targetKcal: number;
  highDays: number;
  /** The swing asked for, as a fraction (0.15 = high days 15% above the daily target). */
  swing: number;
  /** The swing actually used; smaller than asked when a low day would have gone under the floor. */
  appliedSwing: number;
  limitedByFloor: boolean;
  highKcal: number;
  lowKcal: number;
  days: CycleDay[];
  weeklyTotal: number;
  floor: number;
}

export function zigzagPlan(targetKcal: number, floor: number, highDays: 1 | 2 | 3 = 2, swing = 0.15): CyclePlan {
  const base = { targetKcal, highDays, swing, floor };
  const fail = (reason: string): CyclePlan => ({ ...base, ok: false, reason, appliedSwing: 0, limitedByFloor: false, highKcal: 0, lowKcal: 0, days: [], weeklyTotal: 0 });
  if (!(targetKcal > 0)) return fail("Enter your details first.");
  if (!(swing > 0 && swing <= 0.3)) return fail("Choose a swing between 1% and 30%.");
  const lowDays = 7 - highDays;
  const weekly = Math.round(targetKcal) * 7;
  let high = targetKcal * (1 + swing);
  let low = (targetKcal * 7 - highDays * high) / lowDays;
  let limited = false;
  if (low < floor) {
    limited = true;
    high = (targetKcal * 7 - lowDays * floor) / highDays;
    low = floor;
  }
  if (high - targetKcal < 20) return fail(`Your daily target is too close to the safe minimum of ${floor} cal for a cycling plan: there is no room for a higher day. Use the flat daily target.`);
  const hi = Math.round(high), lo = Math.round(low);
  const idx = HIGH_DAY_INDEXES[highDays];
  const days: CycleDay[] = WEEK_DAYS.map((d, i) => ({ day: d, kcal: idx.includes(i) ? hi : lo, high: idx.includes(i) }));
  // Rounding can leave the week a few calories off. Put the difference back one calorie at a time, on lower days first, never taking a lower day under the floor
  // (then on higher days), so the weekly total is exactly the flat plan's and no day breaks the minimum.
  let drift = weekly - days.reduce((s, d) => s + d.kcal, 0);
  const order = [...days.filter((d) => !d.high).reverse(), ...days.filter((d) => d.high)];
  for (let guard = 0; drift !== 0 && guard < 100; guard++) {
    const step = drift > 0 ? 1 : -1;
    const d = order.find((x) => step > 0 || x.kcal - 1 >= floor);
    if (!d) break;
    d.kcal += step; drift -= step;
  }
  return { ...base, ok: true, appliedSwing: (high - targetKcal) / targetKcal, limitedByFloor: limited, highKcal: hi, lowKcal: lo, days, weeklyTotal: days.reduce((s, d) => s + d.kcal, 0) };
}

export function cycleSteps(p: CyclePlan): CalcStep[] {
  if (!p.ok) return [];
  const lowDays = 7 - p.highDays;
  return [
    { label: "Weekly calories", formula: "Daily target x 7 (the week stays the same as the flat plan)", inputs: { "Daily target": `${Math.round(p.targetKcal)} cal` }, result: `${Math.round(p.targetKcal) * 7} cal` },
    { label: "Higher days", formula: p.limitedByFloor ? "Weekly calories - low days x safe minimum, shared over the higher days (swing reduced so low days stay at the minimum)" : "Daily target x (1 + swing)", inputs: { Swing: `${Math.round(p.swing * 100)}%`, "Higher days": p.highDays }, result: `${p.highKcal} cal` },
    { label: "Lower days", formula: "(Weekly calories - higher days x higher-day calories) / lower days", inputs: { "Lower days": lowDays, "Safe minimum": `${p.floor} cal` }, result: `${p.lowKcal} cal` },
    { label: "Check", formula: "Sum of the seven days equals the weekly calories", inputs: { Days: p.days.map((d) => `${d.day} ${d.kcal}`).join(", ") }, result: `${p.weeklyTotal} cal` },
  ];
}
